import type { Key } from 'react';
import type { QnAStateType } from '@uber/react-qna-lib/src';
import type { Theme } from 'baseui';
import clamp from 'lodash/clamp';
import { partitionStaticAndDynamicQuestionnaire } from '../../../common/side-popover-panel/utils';
import type { AnnotationLabelT, AnswerT } from '../../../common/types/types';
import type { RelationshipStoreWithLabelDataT } from '../../../modernization-common/relationships';
import { ANNOTATION_TYPE, DYNAMIC_ATTRIBUTE_TAG } from '../../labelling/constants';
import { CELL_WIDTH, FRAME_AUTOSCROLL_BUFFER_FRAMES, FRAME_LABEL_SUFFIX, MAJOR_TICK_INTERVAL, MINOR_TICKS_PER_MAJOR, ROW_EXPAND_STATE, ROW_ID_SEPARATOR, RULER_EDGE_PADDING, RULER_TICK_KIND, SPAN_POSITION, TIMELINE_ROW_KIND } from '../constants/frame-timeline-v4';
import type { DynamicAnswerSpanT, SpanT } from '../studio-store/types';
import type { AnnotationMetadataRecord } from '../types/annotation';
import type { AnnotationRowDescriptorT, AttributeRowDescriptorT, RowExpandStateT, RulerTickKindT, SpanPositionT, TimelineRowDescriptorT, TrackSpanT } from '../types/frame-timeline-v4';
import { buildSavedRelationshipRows } from './relationship-timeline-rows';

/** Composite id for an attribute row, unique within the timeline. */
export const getAttributeRowId = (annotationId: string, questionId: string): string => `${annotationId}${ROW_ID_SEPARATOR}${questionId}`;

/**
 * Inverse of {@link getAttributeRowId}. Splits on the first separator only, so question ids that
 * themselves contain the separator are preserved intact.
 */
export const parseAttributeRowId = (rowId: string): {
  annotationId: string;
  questionId: string;
} => {
  const separatorIndex = rowId.indexOf(ROW_ID_SEPARATOR);
  if (separatorIndex === -1) {
    return {
      annotationId: rowId,
      questionId: ''
    };
  }
  return {
    annotationId: rowId.slice(0, separatorIndex),
    questionId: rowId.slice(separatorIndex + ROW_ID_SEPARATOR.length)
  };
};

/** Undefined when there are no attributes to reveal; otherwise reflects the expanded flag. */
function getExpandState(hasAttributes: boolean, isExpanded: boolean): RowExpandStateT | undefined {
  if (!hasAttributes) {
    return undefined;
  }
  return isExpanded ? ROW_EXPAND_STATE.EXPANDED : ROW_EXPAND_STATE.COLLAPSED;
}

/** True when the position renders the span's leading edge (left border + rounded corners). */
export const hasStartEdge = (position: SpanPositionT): boolean => position === SPAN_POSITION.START || position === SPAN_POSITION.SINGLE;

/** True when the position renders the span's trailing edge (right border + rounded corners). */
export const hasEndEdge = (position: SpanPositionT): boolean => position === SPAN_POSITION.END || position === SPAN_POSITION.SINGLE;
export type SpanEdgeValuesT = {
  startSide: string;
  endSide: string;
  startRadius: string;
  endRadius: string;
  startInset: string;
  endInset: string;
};

/**
 * Resolves the per-side border, radius, and inset values for a span piece, applying
 * theme tokens only on the edges this position actually renders.
 */
export const getSpanEdgeValues = (theme: Theme, position: SpanPositionT): SpanEdgeValuesT => {
  const edge = theme.sizing.scale0;
  const radius = theme.borders.radius200;
  const gap = theme.sizing.scale0;
  const onStart = hasStartEdge(position);
  const onEnd = hasEndEdge(position);
  return {
    startSide: onStart ? edge : '0px',
    endSide: onEnd ? edge : '0px',
    startRadius: onStart ? radius : '0px',
    endRadius: onEnd ? radius : '0px',
    startInset: onStart ? gap : '0px',
    endInset: onEnd ? gap : '0px'
  };
};

/** Where a frame sits within its span: the only frame, a leading/trailing edge, or interior. */
function getSpanPosition(span: TrackSpanT, frame: number): SpanPositionT {
  if (frame === span.start && frame === span.end) {
    return SPAN_POSITION.SINGLE;
  } else if (frame === span.start) {
    return SPAN_POSITION.START;
  } else if (frame === span.end) {
    return SPAN_POSITION.END;
  } else {
    return SPAN_POSITION.MIDDLE;
  }
}

/** Locates the span covering a frame and where the frame sits within it. */
export function getSpanAtFrame(spans: TrackSpanT[], frame: number): {
  span: TrackSpanT;
  position: SpanPositionT;
} | null {
  const span = spans.find(s => s.start <= frame && s.end >= frame);
  if (!span) {
    return null;
  }
  return {
    span,
    position: getSpanPosition(span, frame)
  };
}
export function buildTimelineRowDescriptors(annotationMetadata: AnnotationMetadataRecord, annotationIdToLabelMap: Map<string, AnnotationLabelT>, expandedAnnotationIds: ReadonlySet<Key>, relationshipStore: RelationshipStoreWithLabelDataT): TimelineRowDescriptorT[] {
  return Array.from(annotationMetadata.entries()).flatMap(([annotationId, metadata]) => {
    const label = annotationIdToLabelMap.get(annotationId);
    const color = label?.style.strokeColor;
    const dynamicAnswers = metadata.dynamicAnswers ?? [];
    const questionIds = getUniqueQuestionIds(dynamicAnswers);
    const relationshipRows = buildSavedRelationshipRows(relationshipStore, annotationIdToLabelMap, annotationId);
    /* `expandedAnnotationIds` already folds in selection (selection implies expansion), so this
     stays purely structural — no selection state leaks into the descriptor identity. */
    const isExpanded = expandedAnnotationIds.has(annotationId);
    const hasChildren = questionIds.length > 0 || relationshipRows.length > 0;
    const expandState = getExpandState(hasChildren, isExpanded);

    /* Fall back to the raw labelId when no display title exists, and capitalize so row labels
     read consistently regardless of how the source title was cased. */
    const rawTitle = label?.title ?? metadata.labelId;
    const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);
    const annotationRow: AnnotationRowDescriptorT = {
      kind: TIMELINE_ROW_KIND.ANNOTATION,
      id: annotationId,
      annotationId,
      name: `${title} [${annotationId}]`,
      color,
      spans: toAnnotationSpans(metadata.spans, metadata.annotationType, metadata.answers),
      keyframes: metadata.keyframes,
      expandState,
      label,
      annotationType: metadata.annotationType
    };
    if (expandState !== ROW_EXPAND_STATE.EXPANDED) {
      return [annotationRow];
    }
    const attributeRows: AttributeRowDescriptorT[] = questionIds.map(questionId => ({
      kind: TIMELINE_ROW_KIND.ATTRIBUTE,
      id: getAttributeRowId(annotationId, questionId),
      annotationId,
      name: getQuestionName(dynamicAnswers, questionId) ?? questionId,
      color,
      spans: toAttributeSpans(dynamicAnswers, questionId)
    }));
    return [annotationRow, ...attributeRows, ...relationshipRows];
  });
}
function getUniqueQuestionIds(dynamicAnswers: DynamicAnswerSpanT[]): string[] {
  const seen = new Set<string>();
  for (const segment of dynamicAnswers) {
    for (const questionId of Object.keys(segment.answers)) {
      seen.add(questionId);
    }
  }
  return Array.from(seen);
}
function getQuestionName(dynamicAnswers: DynamicAnswerSpanT[], questionId: string): string | undefined {
  for (const segment of dynamicAnswers) {
    const answer = segment.answers[questionId];
    if (answer?.question) {
      return answer.question;
    }
  }
}

/** Joins a single question's answers into a comma-separated string, dropping non-string (File) values. */
function formatAnswerValue(answer: AnswerT[string]): string {
  return answer.answers.filter((a): a is string => typeof a === 'string').join(', ');
}

/** Flattens every question's answers for an annotation into one display string. */
export function formatAnswers(answers: AnswerT | undefined): string {
  if (!answers) {
    return '';
  }
  return Object.values(answers).map(formatAnswerValue).filter(Boolean).join(', ');
}
function toAnnotationSpans(spans: TrackSpanT[], annotationType: string, answers: AnswerT | undefined): TrackSpanT[] {
  if (annotationType !== ANNOTATION_TYPE.FRAME_CLASSIFICATION) {
    return spans;
  }
  const value = formatAnswers(answers);
  if (!value) {
    return spans;
  }
  return spans.map(span => ({
    ...span,
    value
  }));
}
function toAttributeSpans(dynamicAnswers: DynamicAnswerSpanT[], questionId: string): TrackSpanT[] {
  return dynamicAnswers.reduce<TrackSpanT[]>((acc, segment) => {
    const answer = segment.answers[questionId];
    if (!answer) {
      return acc;
    }
    const value = formatAnswerValue(answer);
    /* Merge with the previous span when it holds the same value and is contiguous, so extending
    an attribute reads as one continuous span instead of a new one abutting the old. */
    const last = acc[acc.length - 1];
    if (last && last.value === value && segment.frameSpan.start <= last.end + 1) {
      const combined: TrackSpanT = {
        start: last.start,
        end: Math.max(last.end, segment.frameSpan.end),
        value
      };
      return [...acc.slice(0, -1), combined];
    }
    return [...acc, {
      start: segment.frameSpan.start,
      end: segment.frameSpan.end,
      value
    }];
  }, []);
}
export function getFrameCellCenterX(frame: number, cellWidth: number = CELL_WIDTH): number {
  return RULER_EDGE_PADDING + (frame + 1) * cellWidth + cellWidth / 2;
}
export function getAutoScrollTarget(frame: number, direction: 1 | -1, totalFrames: number, bufferFrames: number = FRAME_AUTOSCROLL_BUFFER_FRAMES): number {
  // Frame f lives in column f + 1 (column 0 is the empty "0f" origin).
  return clamp(frame + 1 + direction * bufferFrames, 0, totalFrames);
}
export function columnIndexToFrame(columnIndex: number): number {
  return columnIndex - 1;
}
export function getMarkerTransform(frame: number, cellWidth: number = CELL_WIDTH): string {
  return `translateX(${getFrameCellCenterX(frame, cellWidth)}px)`;
}
export function formatFrameBadge(frame: number): string {
  return String(frame + 1);
}
export function formatFrameLabel(frame: number): string {
  return `${frame}${FRAME_LABEL_SUFFIX}`;
}
export function classifyRulerTick(frame: number, options: {
  majorInterval?: number;
  minorPerMajor?: number;
} = {}): RulerTickKindT | null {
  const {
    majorInterval = MAJOR_TICK_INTERVAL,
    minorPerMajor = MINOR_TICKS_PER_MAJOR
  } = options;
  if (frame < 0 || majorInterval <= 0) {
    return null;
  }
  if (frame % majorInterval === 0) {
    return RULER_TICK_KIND.MAJOR;
  }
  const minorStep = majorInterval / (minorPerMajor + 1);
  if (Number.isInteger(minorStep) && minorStep > 0 && frame % minorStep === 0) {
    return RULER_TICK_KIND.MINOR;
  }
  return null;
}
export function buildDefaultDynamicAnswers(labelLevelQuestionnaire?: QnAStateType): AnswerT | undefined {
  if (!labelLevelQuestionnaire) return undefined;
  const {
    dynamicQuestionnaire
  } = partitionStaticAndDynamicQuestionnaire(labelLevelQuestionnaire);
  const combinedAnswers: AnswerT = {};
  for (const [questionId, question] of Object.entries(dynamicQuestionnaire.questions ?? {})) {
    const firstAnswer = question.answers?.[0]?.answer;
    if (typeof firstAnswer !== 'string') continue;
    combinedAnswers[questionId] = {
      answers: [firstAnswer],
      question: question.question ?? '',
      tags: [DYNAMIC_ATTRIBUTE_TAG]
    };
  }
  return Object.keys(combinedAnswers).length > 0 ? combinedAnswers : undefined;
}
export function findContainingSpan(spans: SpanT[] | undefined, frameIndex: number): SpanT | undefined {
  return spans?.find(span => span.start <= frameIndex && span.end >= frameIndex);
}